import { useEffect, useRef, useState } from "react";
import type { Settings } from "@vereinfach/types";
import { apiUrl, errorMessage, mutate } from "../../lib/api";
import { useSettings as useSettingsContext } from "../../lib/settings";
import { useAuth } from "../../lib/auth";
import { useCurrentTenant } from "../../lib/currentTenant";
import { Field, SaveBar, Section } from "./common";

export function SettingsVerein() {
  const { settings, refetch: refetchSettings } = useSettingsContext();
  const { refetchMe } = useAuth();
  const tenant = useCurrentTenant();

  const [local, setLocal] = useState<Settings | null>(null);
  const [slug, setSlug] = useState(tenant.slug);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (settings) setLocal(settings);
  }, [settings]);

  useEffect(() => {
    setSlug(tenant.slug);
  }, [tenant.slug]);

  function set<K extends keyof Settings>(k: K, v: Settings[K]) {
    setLocal((s) => (s ? { ...s, [k]: v } : s));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!local) return;
    setSaving(true);
    setError(null);
    try {
      await mutate<Settings>(
        `/tenants/${tenant.id}/settings`,
        {
          club_name: local.club_name,
          slug: slug !== tenant.slug ? slug : undefined,
          address_line1: local.address_line1,
          address_line2: local.address_line2,
          zip: local.zip,
          city: local.city,
          country: local.country,
          creditor_name_override: local.creditor_name_override,
        },
        "PATCH",
      );
      await Promise.all([refetchSettings(), refetchMe()]);
      setSavedAt(Date.now());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function uploadLogo(file: File) {
    await fetch(apiUrl(`/tenants/${tenant.id}/settings/logo`), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": file.type },
      body: file,
    });
    await refetchSettings();
  }

  if (!local) return <p className="text-slate-400">Lädt…</p>;

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      <Section title="Verein">
        <Field label="Name">
          <input
            required
            value={local.club_name}
            onChange={(e) => set("club_name", e.target.value)}
            className="input"
          />
        </Field>
        <Field
          label="Kurzname (Slug)"
          help="Nur Kleinbuchstaben, Ziffern und Bindestriche. Wird für künftige öffentliche Links verwendet."
        >
          <input
            required
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            pattern="[a-z0-9\-]+"
            className="input font-mono text-sm"
          />
        </Field>
      </Section>
      <Section title="Rechnungsadresse">
        <p className="text-sm text-slate-500">
          Diese Adresse erscheint auf Rechnungen. Du kannst sie unabhängig von IBAN und Zahlungsart
          speichern.
        </p>
        <Field
          label="Strasse und Nummer"
          help="Pflichtfeld für QR-Rechnung. Zusammen mit PLZ und Ort wird diese Adresse auf der QR-Rechnung verwendet."
        >
          <input
            value={local.address_line1 ?? ""}
            onChange={(e) => set("address_line1", e.target.value || null)}
            className="input"
          />
        </Field>
        <Field label="Adresszusatz (optional)">
          <input
            value={local.address_line2 ?? ""}
            onChange={(e) => set("address_line2", e.target.value || null)}
            className="input"
          />
        </Field>
        <div className="grid grid-cols-3 gap-4">
          <Field label="PLZ" help="Pflichtfeld für QR-Rechnung.">
            <input
              value={local.zip ?? ""}
              onChange={(e) => set("zip", e.target.value || null)}
              className="input"
            />
          </Field>
          <div className="col-span-2">
            <Field label="Ort" help="Pflichtfeld für QR-Rechnung.">
              <input
                value={local.city ?? ""}
                onChange={(e) => set("city", e.target.value || null)}
                className="input"
              />
            </Field>
          </div>
        </div>

        <Field
          label="Zahlungsempfänger (falls abweichend)"
          help="Standard: Vereinsname. Erscheint auf dem Einzahlungsschein."
        >
          <input
            value={local.creditor_name_override ?? ""}
            onChange={(e) => set("creditor_name_override", e.target.value || null)}
            placeholder={local.club_name}
            className="input"
          />
        </Field>

        <Field label="Logo">
          <div className="flex items-center gap-3">
            {local.logo_r2_key && (
              <img
                src={apiUrl(`/tenants/${tenant.id}/settings/logo`)}
                alt="Logo"
                className="h-12 w-12 rounded object-contain border border-slate-200"
              />
            )}
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="text-sm"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadLogo(f);
              }}
            />
          </div>
        </Field>
      </Section>

      <SaveBar saving={saving} savedAt={savedAt} error={error} />
    </form>
  );
}
